import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowRight, BookOpen, FileText, Boxes, Briefcase, Bug, Building2, ChevronDown, Compass, Globe, LayoutGrid, Library, Lightbulb, ListChecks,
  Lock, Mail, Menu, MessageCircle, MessageSquareText, Plug, Rocket, ShieldCheck, Users, Workflow, X,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { liveProducts } from "@/data/products";
import {
  ANNOUNCEMENT,
  GET_STARTED_URL,
  LOGIN_URL,
  REQUEST_DEMO_URL,
  languages,
  mainNav,
  type NavGroup,
  type NavLinkItem,
} from "@/data/site";

// Anything outside the marketing app (login, the workspace) needs a full page load.
const isAppUrl = (href: string) => /^\/(login|platform|home|api)\b/.test(href) || /^https?:/.test(href);

/**
 * Signed in, "Dashboard" could only mean one product. With two, the button
 * opens the list instead: every product, with its own mark, in one place.
 */
const OpenAppMenu = () => {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={box}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="cm-btn h-10 gap-1.5 whitespace-nowrap rounded-xl bg-[hsl(0_0%_4%)] px-4 text-[15px] text-background hover:bg-[hsl(0_0%_20%)]"
      >
        Open app
        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            role="menu"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.16 }}
            className="absolute right-0 top-full z-50 mt-2 w-[280px] rounded-2xl border border-border bg-background p-2 shadow-[0_24px_50px_-25px_hsl(0_0%_0%/0.45)]"
          >
            <p className="px-2.5 pb-1 pt-1.5 text-[11.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              Your apps
            </p>
            {liveProducts.map((p) => (
              <a
                key={p.slug}
                role="menuitem"
                href={p.appUrl}
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 rounded-xl px-2.5 py-2 transition-colors hover:bg-muted"
              >
                <img src={p.logo} alt="" width={26} height={26} className="h-[26px] w-[26px] object-contain" />
                <span className="min-w-0">
                  <span className="block text-[14px] font-semibold text-foreground">{p.name}</span>
                  <span className="block text-[12.5px] leading-snug text-muted-foreground">{p.shortLine}</span>
                </span>
              </a>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const SmartLink = ({
  href,
  className,
  children,
  onClick,
}: {
  href: string;
  className?: string;
  children: React.ReactNode;
  onClick?: () => void;
}) =>
  isAppUrl(href) ? (
    <a href={href} className={className} onClick={onClick}>
      {children}
    </a>
  ) : (
    <Link to={href} className={className} onClick={onClick}>
      {children}
    </Link>
  );

// A plain black line icon for every menu link; products show their own mark in black.
const MENU_ICONS: Record<string, typeof Globe> = {
  "All products": LayoutGrid,
  Features: ListChecks,
  Integrations: Plug,
  Workflows: Workflow,
  Security: ShieldCheck,
  "Website chatbot": MessageCircle,
  "Internal assistant": Users,
  "MCP and apps": Boxes,
  "Use cases": Lightbulb,
  Founders: Rocket,
  "Remote teams": Globe,
  Freelancers: Briefcase,
  "Smart FAQ": MessageSquareText,
  "All solutions": Compass,
  Documentation: BookOpen,
  Guides: FileText,
  "Zapier alternatives": Compass,
  Resources: Library,
  "Why ClearMakes": Compass,
  "Security architecture": ShieldCheck,
  Privacy: Lock,
  "Report a vulnerability": Bug,
  About: Building2,
  Careers: Briefcase,
  Contact: Mail,
};
const markFor = (label: string) => liveProducts.find((p) => p.name === label)?.logo;

// No dark border on hover (the owner, 2026-10-06): the row gets its soft grey, the
// icon box stays light.
const MenuIcon = ({ l }: { l: NavLinkItem }) => {
  const mark = l.icon ?? markFor(l.label);
  const Icon = MENU_ICONS[l.label] ?? ArrowRight;
  return (
    <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border bg-background shadow-[0_1px_2px_hsl(0_0%_0%/0.04)] transition-colors group-hover:bg-white${mark ? "" : " text-muted-foreground group-hover:text-foreground"}`}>
      {mark ? (
        <img src={mark} alt="" width={18} height={18} className="h-[18px] w-[18px] object-contain" />
      ) : (
        <Icon className="h-4 w-4" strokeWidth={1.8} />
      )}
    </span>
  );
};

const MenuColumn = ({ heading, links, onNavigate }: { heading: string; links: NavLinkItem[]; onNavigate: () => void }) => (
  <div className="min-w-[240px]">
    <p className="px-2.5 pb-2 text-[11.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{heading}</p>
    <ul className="space-y-0.5">
      {links.map((l) => (
        <li key={l.href + l.label}>
          <SmartLink
            href={l.href}
            onClick={onNavigate}
            className="group flex items-start gap-3 rounded-xl px-2.5 py-2 transition-colors hover:bg-[hsl(216_40%_96%)]"
          >
            <MenuIcon l={l} />
            <span className="block min-w-0 self-center">
              <span className="block text-[14.5px] font-medium text-foreground">{l.label}</span>
              {l.description && <span className="mt-0.5 block text-[13px] leading-snug text-muted-foreground">{l.description}</span>}
            </span>
          </SmartLink>
        </li>
      ))}
    </ul>
  </div>
);

export const LanguageMenu = ({ pill = false }: { pill?: boolean }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const translate = (code: string) => {
    setOpen(false);
    if (code === "en") return;
    window.location.href = `https://translate.google.com/translate?sl=en&tl=${code}&u=${encodeURIComponent(window.location.href)}`;
  };
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-label="Change language"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={pill ? "inline-flex h-9 items-center gap-2 rounded-full border border-border px-3.5 text-[14px] text-foreground transition-colors hover:bg-muted" : "cm-btn-ghost h-9 px-2.5 text-[14px] text-muted-foreground"}
      >
        <Globe className="h-4 w-4" />
        <span className={pill ? "" : "hidden xl:inline"}>English</span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.14 }}
            className={`absolute z-50 w-52 ${pill ? "bottom-full left-0 mb-2 lg:left-auto lg:right-0" : "right-0 top-full mt-2"} rounded-xl border border-border bg-popover p-1.5 shadow-[var(--shadow-card)]`}
          >
            {languages.map((l) => (
              <button
                key={l.code}
                type="button"
                onClick={() => translate(l.code)}
                className="flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-left text-[14px] text-foreground hover:bg-muted"
              >
                {l.label}
                {l.code === "en" && <span className="text-xs text-muted-foreground">Current</span>}
              </button>
            ))}
            <p className="border-t border-border px-2.5 pb-1 pt-2 text-xs leading-snug text-muted-foreground">
              Other languages open an automatic translation.
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* The strip on top of the menu: blue, and it turns ink when you point at it. */
/** The Windows logo (four panes), drawn in one colour. */
export const WindowsLogo = ({ className = "h-3.5 w-3.5" }: { className?: string }) => (
  <svg viewBox="0 0 88 88" aria-hidden className={className} fill="currentColor">
    <path d="M0 12.4 35.7 7.5v34.5H0zM40 6.9 87.3 0v41.6H40zM0 45.7h35.7v34.6L0 75.4zM40 46.2h47.3V88L40 81.3z" />
  </svg>
);

/* The strip on top of the menu: blue, and it turns ink when you point at it. Everything
   on it stays white in both states, so nothing blue flashes while the colour changes. */
const AnnouncementBar = () => (
  <SmartLink
    href={ANNOUNCEMENT.href}
    className="group flex h-10 items-center justify-between gap-4 bg-[hsl(216_84%_52%)] px-4 text-background transition-colors duration-200 hover:bg-[hsl(0_0%_4%)] sm:px-6"
  >
    <span className="flex min-w-0 items-center gap-3">
      <span className="shrink-0 rounded-md border border-background/40 px-1.5 py-px text-[11.5px] font-bold uppercase tracking-[0.08em]">
        {ANNOUNCEMENT.tag}
      </span>
      <WindowsLogo className="h-3.5 w-3.5 shrink-0" />
      <span className="truncate text-[14px] font-medium sm:text-[15px]">
        <span className="font-semibold">{ANNOUNCEMENT.title}</span>
        <span className="hidden text-background/80 sm:inline"> {ANNOUNCEMENT.line}</span>
      </span>
    </span>
    <span className="flex shrink-0 items-center gap-1.5 text-[12.5px] font-bold uppercase tracking-[0.08em]">
      {ANNOUNCEMENT.cta} <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-1" />
    </span>
  </SmartLink>
);

export const Header = () => {
  const { pathname } = useLocation();
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileGroup, setMobileGroup] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();
  // A pointer opens a menu on hover; the click that usually follows must not close it.
  const openedAt = useRef(0);
  const navRef = useRef<HTMLElement>(null);

  // Close an open menu when someone taps or clicks elsewhere (touch screens have no hover).
  useEffect(() => {
    if (!openMenu) return;
    const onDown = (e: MouseEvent) => navRef.current && !navRef.current.contains(e.target as Node) && setOpenMenu(null);
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpenMenu(null);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [openMenu]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close everything on navigation.
  useEffect(() => {
    setOpenMenu(null);
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  // Signed-in visitors get a way back into their workspace instead of "Log in".
  useEffect(() => {
    fetch("/api/auth/status", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setSignedIn(!!(d && (d.logged_in || d.authenticated || d.user))))
      .catch(() => undefined);
  }, []);

  const hoverOpen = (label: string) => {
    clearTimeout(closeTimer.current);
    setOpenMenu((m) => {
      if (m !== label) openedAt.current = Date.now();
      return label;
    });
  };
  const hoverClose = () => {
    closeTimer.current = setTimeout(() => setOpenMenu(null), 120);
  };

  const navItem = "inline-flex h-10 items-center gap-1 whitespace-nowrap rounded-xl px-3 text-[15px] xl:px-3.5 font-medium text-foreground transition-colors hover:bg-[hsl(40_9%_95%)]";

  const renderGroup = (g: NavGroup) =>
    g.href ? (
      <SmartLink key={g.label} href={g.href} className={navItem}>
        {g.label}
      </SmartLink>
    ) : (
      <div key={g.label} className="relative" onMouseEnter={() => hoverOpen(g.label)} onMouseLeave={hoverClose}>
        <button
          type="button"
          aria-expanded={openMenu === g.label}
          onClick={() =>
            setOpenMenu((m) => {
              if (m === g.label && Date.now() - openedAt.current > 500) return null;
              openedAt.current = Date.now();
              return g.label;
            })
          }
          className={`${navItem} ${openMenu === g.label ? "bg-[hsl(40_9%_95%)]" : ""}`}
        >
          {g.label}
          <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform duration-200 ${openMenu === g.label ? "rotate-180" : ""}`} />
        </button>
        <AnimatePresence>
          {openMenu === g.label && (
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
              className="absolute left-0 top-full z-50 pt-3"
            >
              <div className="flex gap-3 rounded-2xl border border-border bg-popover p-3 shadow-[0_30px_60px_-30px_hsl(0_0%_0%/0.4)]">
                {g.columns!.map((c) => (
                  <MenuColumn key={c.heading} heading={c.heading} links={c.links} onNavigate={() => setOpenMenu(null)} />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );

  return (
    // A full-width bar across the top of the page: the blue strip, then the menu.
    // It stays with you as you scroll.
    <header className="sticky top-0 z-40 w-full">
      <div
        className={`relative w-full overflow-visible border-b border-border bg-background transition-shadow duration-300 ${
          scrolled || mobileOpen ? "shadow-[0_16px_40px_-24px_hsl(0_0%_0%/0.35)]" : ""
        }`}
      >
        <div className="overflow-hidden">
          <AnnouncementBar />
        </div>
        <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-10">
          <div className="flex items-center gap-4 xl:gap-8">
            <Link to="/" aria-label="ClearMakes home" className="flex items-center">
              <Logo size={28} />
            </Link>
            <nav ref={navRef} className="hidden items-center gap-0.5 lg:flex" aria-label="Main">
              {mainNav.map(renderGroup)}
            </nav>
          </div>

          <div className="hidden items-center gap-2 lg:flex">
            {!signedIn && (
              <a href={LOGIN_URL} className="inline-flex h-10 items-center whitespace-nowrap rounded-xl px-3.5 text-[15px] font-medium text-foreground transition-colors hover:bg-[hsl(40_9%_95%)]">
                Log in
              </a>
            )}
            <SmartLink href={REQUEST_DEMO_URL} className="hidden h-10 items-center whitespace-nowrap rounded-xl bg-[hsl(40_9%_94%)] xl:inline-flex px-4 text-[15px] font-medium text-foreground transition-colors hover:bg-[hsl(40_9%_89%)]">
              Request a demo
            </SmartLink>
            {signedIn ? (
              <OpenAppMenu />
            ) : (
              <SmartLink href={GET_STARTED_URL} className="inline-flex h-10 items-center whitespace-nowrap rounded-xl bg-[hsl(0_0%_4%)] px-4 text-[15px] font-semibold text-background transition-colors hover:bg-[hsl(216_84%_52%)]">
                Get started
              </SmartLink>
            )}
          </div>

          <div className="flex items-center gap-1 lg:hidden">
            <SmartLink href={GET_STARTED_URL} className="inline-flex h-9 items-center whitespace-nowrap rounded-lg bg-[hsl(0_0%_4%)] px-3.5 text-[14px] font-semibold text-background">
              Get started
            </SmartLink>
            <button
              type="button"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((o) => !o)}
              className="cm-btn-ghost h-9 w-9 px-0"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="absolute inset-x-0 top-full z-40 mt-2 h-[calc(100dvh-8rem)] overflow-y-auto rounded-2xl border border-border bg-background lg:hidden"
            >
              <div className="px-5 pb-10 pt-2">
                {mainNav.map((g) =>
                  g.href ? (
                    <SmartLink key={g.label} href={g.href} className="flex h-12 items-center border-b border-border text-[17px] font-medium">
                      {g.label}
                    </SmartLink>
                  ) : (
                    <div key={g.label} className="border-b border-border">
                      <button
                        type="button"
                        onClick={() => setMobileGroup((m) => (m === g.label ? null : g.label))}
                        className="flex h-12 w-full items-center justify-between text-[17px] font-medium"
                        aria-expanded={mobileGroup === g.label}
                      >
                        {g.label}
                        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${mobileGroup === g.label ? "rotate-180" : ""}`} />
                      </button>
                      <AnimatePresence initial={false}>
                        {mobileGroup === g.label && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                          >
                            <div className="space-y-4 pb-4">
                              {g.columns!.map((c) => (
                                <div key={c.heading}>
                                  <p className="pb-1 text-[11.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{c.heading}</p>
                                  {c.links.map((l) => (
                                    <SmartLink key={l.href + l.label} href={l.href} className="block py-1.5 text-[15px] text-foreground">
                                      {l.label}
                                    </SmartLink>
                                  ))}
                                </div>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ),
                )}
                <div className="mt-6 grid gap-2">
                  <SmartLink href={GET_STARTED_URL} className="cm-btn-primary cm-btn-lg w-full">Get started</SmartLink>
                  <SmartLink href={REQUEST_DEMO_URL} className="cm-btn-secondary cm-btn-lg w-full">Request a demo</SmartLink>
                  {signedIn ? (
                    liveProducts.map((p) => (
                      <a key={p.slug} href={p.appUrl} className="cm-btn-ghost cm-btn-lg w-full gap-2">
                        <img src={p.logo} alt="" width={20} height={20} className="h-5 w-5 object-contain" />
                        Open {p.name.replace("ClearMakes ", "")}
                      </a>
                    ))
                  ) : (
                    <a href={LOGIN_URL} className="cm-btn-ghost cm-btn-lg w-full">Log in</a>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};
